// ─── DataSim Risk Analyzer — Shared Types (v2.0) ──────────────────────────────

export type ProjectType =
  | 'regulatory' | 'software' | 'ops_transformation' | 'm_and_a'
  | 'marketing' | 'infrastructure' | 'custom';

export type MetricType = 'duration' | 'cost' | 'effort' | 'custom';
export type SimScreen = 'setup' | 'intake' | 'constraints' | 'scenarios' | 'results';
export type AIEligibility = 'high' | 'medium' | 'low' | 'none';
export type ReadinessTier = 0 | 1 | 2 | 3 | 4;

// ─── Setup ────────────────────────────────────────────────────────────────────

export interface ProjectSetup {
  projectName: string;
  projectType: ProjectType;
  customProjectType?: string;
  metricType: MetricType;
  metricUnit: string;
  metricLabel: string;
  uploadedDocText?: string; // Phase 5: extracted text from uploaded document
  projectStartDate?: string; // ISO date (YYYY-MM-DD) — anchors week durations to calendar dates
}

// ─── Chat ─────────────────────────────────────────────────────────────────────

export interface Message {
  role: 'user' | 'assistant';
  content: string;
}

// ─── Manifest ─────────────────────────────────────────────────────────────────

export interface ManifestVariable {
  id: string;
  name: string;
  description?: string;
  team_id?: string;
  type: string;
  distribution?: {
    type: string;
    params: { optimistic?: number; most_likely?: number; pessimistic?: number };
  };
  unit?: string;
  applied_constraints?: string[];
  ai_eligibility?: AIEligibility;
  confidence?: 'high' | 'medium' | 'low';
  notes?: string;
  source?: 'user_stated' | 'ai_estimated' | 'historical';
}

export interface ManifestTeam {
  id: string;
  name: string;
  type: 'internal' | 'vendor' | 'external_partner' | 'regulatory_body' | 'mixed';
  size?: number;
  availability_pct?: number;
  start_date_offset_weeks?: number;
  ai_readiness_tier?: ReadinessTier;
  notes?: string;
}

export interface InterTeamDependency {
  from_team: string;
  from_variable?: string;
  to_team: string;
  to_variable?: string;
  handoff_risk: 'low' | 'medium' | 'high';
}

export interface ExternalDependency {
  id: string;
  name: string;
  owner?: string;
  probability_on_time?: number;
  if_late_distribution?: {
    type: string;
    params: { optimistic?: number; most_likely?: number; pessimistic?: number };
  };
  unit?: string;
  historical_reliability?: 'high' | 'medium' | 'low' | 'unknown';
}

export interface Manifest {
  project_name?: string;
  project_type?: string;
  primary_output_metric?: { type: string; unit?: string; label?: string };
  teams?: ManifestTeam[];
  program_topology?: {
    structure?: 'sequential' | 'parallel' | 'hybrid';
    inter_team_dependencies?: InterTeamDependency[];
    parallel_tracks?: Array<{ track_name: string; teams: string[]; converges_at?: string }>;
  };
  variables?: ManifestVariable[];
  external_dependencies?: ExternalDependency[];
  constraints_applied?: string[];
  constraint_config?: {
    planning_fallacy?: { inflation_factor?: number; applied_to?: string[] };
    scope_creep_factor?: { inflation_pct?: number };
    parkinson?: { enabled?: boolean };
    student_syndrome?: { enabled?: boolean };
    external_deps?: { enabled?: boolean };
    key_person?: { enabled?: boolean };
    brooks_law?: { enabled?: boolean; applied_to_teams?: string[] };
    [key: string]: any;
  };
  completeness_audit?: {
    required_complete?: boolean;
    coverage_score?: number;
    open_gaps?: string[];
  };
  interview_phase?: string;
  interview_summary?: string;
}

// ─── Constraints ──────────────────────────────────────────────────────────────

export interface ConstraintConfig {
  planning_fallacy: { enabled: boolean; inflation_factor: number };
  scope_creep: { enabled: boolean; inflation_pct: number };
  parkinson: { enabled: boolean };
  student_syndrome: { enabled: boolean };
  external_deps: { enabled: boolean };
  key_person: { enabled: boolean };
}

export const DEFAULT_CONSTRAINTS: ConstraintConfig = {
  planning_fallacy: { enabled: true, inflation_factor: 0.25 },
  scope_creep: { enabled: false, inflation_pct: 0.15 },
  parkinson: { enabled: false },
  student_syndrome: { enabled: false },
  external_deps: { enabled: true },
  key_person: { enabled: true },
};

// ─── AI Scenario ──────────────────────────────────────────────────────────────

export const READINESS_TIER_LABELS: Record<ReadinessTier, string> = {
  0: 'No Adoption',
  1: 'Ad Hoc',
  2: 'Emerging',
  3: 'Integrated',
  4: 'Advanced',
};

export const READINESS_TIER_DESCRIPTIONS: Record<ReadinessTier, string> = {
  0: 'No AI tools in current workflows',
  1: 'A few individuals using AI informally; no standardized prompts or workflows',
  2: 'Team-level adoption; some standardized use cases; tooling in place',
  3: 'AI embedded in core workflows; prompt libraries maintained; output review processes established',
  4: 'Custom AI solutions, automation pipelines, AI-in-the-loop for review and QA',
};

export const READINESS_ACCELERATION: Record<ReadinessTier, Record<string, [number, number] | 0>> = {
  0: { high: 0, medium: 0, low: 0 },
  1: { high: [0.05, 0.15], medium: 0, low: 0 },
  2: { high: [0.10, 0.25], medium: [0.05, 0.10], low: 0 },
  3: { high: [0.25, 0.40], medium: [0.10, 0.20], low: [0, 0.05] },
  4: { high: [0.35, 0.55], medium: [0.20, 0.35], low: [0.05, 0.15] },
};

export interface TeamReadinessAssessment {
  team_id: string;
  team_name: string;
  readiness_tier: ReadinessTier;
  ai_adoption_start_offset_weeks?: number;
}

export interface VariableEligibilityOverride {
  variable_id: string;
  ai_eligibility: AIEligibility;
  eligibility_rationale?: string;
}

export interface AIScenarioConfig {
  readiness_assessments: TeamReadinessAssessment[];
  variable_overrides: VariableEligibilityOverride[];
  include_downside_risk: boolean;
  downside_probability?: number; // probability of rework per AI-eligible task
}

// ─── Simulation Results ───────────────────────────────────────────────────────

export interface TeamSimResult {
  team_id: string;
  team_name: string;
  p50_completion: number;
  p90_completion: number;
  probability_on_critical_path: number;
  mean_handoff_delay: number;
}

export interface SimResults {
  primary_metric: {
    p10: number; p25: number; p50: number; p75: number;
    p90: number; mean: number; std_dev: number;
  };
  sensitivity_rankings: Array<{
    variable_id: string; variable_name: string;
    unit?: string; spearman_r: number;
  }>;
  histogram: Array<{ bin: number; count: number }>;
  iterations: number;
  variable_count: number;
  constraints_applied?: string[];
  team_results?: TeamSimResult[];
  // AI Impact comparison (present when AI scenario was run)
  ai_impact?: {
    primary_metric: {
      p10: number; p25: number; p50: number; p75: number;
      p90: number; mean: number; std_dev: number;
    };
    histogram: Array<{ bin: number; count: number }>;
    team_results?: TeamSimResult[];
    downside_triggered_pct?: number;
  };
  readiness_sensitivity?: Array<{
    tier: ReadinessTier;
    tier_label: string;
    p50: number;
    p90: number;
  }>;
  workload_check?: WorkloadCheck;
}

// ─── Workload check ───────────────────────────────────────────────────────────

export interface WorkloadTeamLoad {
  team_id: string;
  team_name: string;
  size: number;
  allocation_pct: number;
  allocation_assumed: boolean;
  hours_per_person: number;
  team_weekly_total: number;
  pass: boolean;
}

export interface WorkloadCheck {
  applicable: boolean;
  reason?: string;
  ceiling_hours?: number;
  standard_week_hours?: number;
  pass?: boolean;
  team_weekly_total?: number;
  overloaded_team_count?: number;
  per_team?: WorkloadTeamLoad[];
}

// ─── Scenario comparison ──────────────────────────────────────────────────────

export interface ScenarioLevers {
  add_team_members?: number;       // count of people added
  reduce_scope_pct?: number;       // 0-60
  reduce_variability_pct?: number; // 0-80
  better_training_pct?: number;    // 0-30
  reduce_sme_deps_pct?: number;    // 0-50
}

export interface ScenarioDefinition {
  name: string;
  levers: ScenarioLevers;
  ai_scenario?: AIScenarioConfig | null;
}

export interface ScenarioResult {
  name: string;
  is_baseline: boolean;
  levers?: ScenarioLevers;
  p10: number;
  p50: number;
  p90: number;
  mean: number;
  delta_p50?: number;
  delta_p90?: number;
}

export interface ScenarioBatchResponse {
  baseline: ScenarioResult;
  scenarios: ScenarioResult[];
  iterations: number;
}
